import { isPaletteId, type PaletteId } from './palettes';
import { storageKey } from './edition';
import { storage } from './storage';

export type MotionPref = 'system' | 'reduced' | 'full';

interface SettingsData {
  palette: PaletteId;
  sound: boolean;
  volume: number;
  haptics: boolean;
  motion: MotionPref;
}

const KEY = storageKey('settings');
const DEFAULTS: SettingsData = {
  palette: 'auto',
  sound: true,
  volume: 0.7,
  haptics: true,
  motion: 'system',
};

function load(): SettingsData {
  try {
    const raw = JSON.parse(storage.get(KEY) ?? '{}') as Partial<SettingsData>;
    return {
      palette: isPaletteId(raw.palette) ? raw.palette : DEFAULTS.palette,
      sound: typeof raw.sound === 'boolean' ? raw.sound : DEFAULTS.sound,
      volume:
        typeof raw.volume === 'number' ? Math.min(1, Math.max(0, raw.volume)) : DEFAULTS.volume,
      haptics: typeof raw.haptics === 'boolean' ? raw.haptics : DEFAULTS.haptics,
      motion: raw.motion === 'reduced' || raw.motion === 'full' ? raw.motion : DEFAULTS.motion,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

/** Player preferences. Kept apart from the game save so a progress reset keeps them. */
class Settings {
  palette = $state<PaletteId>(DEFAULTS.palette);
  sound = $state(DEFAULTS.sound);
  volume = $state(DEFAULTS.volume);
  haptics = $state(DEFAULTS.haptics);
  motion = $state<MotionPref>(DEFAULTS.motion);
  #systemReduced = $state(false);

  constructor() {
    Object.assign(this, load());
    if (typeof matchMedia === 'function') {
      const mq = matchMedia('(prefers-reduced-motion: reduce)');
      this.#systemReduced = mq.matches;
      mq.addEventListener('change', (e) => (this.#systemReduced = e.matches));
    }
  }

  /** Whether animations should be minimised right now. */
  get reducedMotion(): boolean {
    return this.motion === 'reduced' || (this.motion === 'system' && this.#systemReduced);
  }

  persist(): void {
    const data: SettingsData = {
      palette: this.palette,
      sound: this.sound,
      volume: this.volume,
      haptics: this.haptics,
      motion: this.motion,
    };
    storage.set(KEY, JSON.stringify(data));
  }
}

export const settings = new Settings();
